import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';
import colors, { type AccentColor } from '../colors';
import { MascotMap, MascotName } from './generated/svg';

const AvatarVariants = cva(`rounded-full border shrink-0 aspect-square overflow-hidden`, {
  variants: {
    variant: {
      default:
        'flex  items-center justify-center bg-primary-base border-primary-base text-foreground-white font-semibold',
      inverted:
        'flex items-center justify-center bg-background-input border-background-input text-primary-base font-semibold'
    },
    accent: {
      first: 'bg-accent-first/40 border-accent-first/40',
      second: 'bg-accent-second/40 border-accent-second/40',
      third: 'bg-accent-third/40 border-accent-third/40',
      fourth: 'bg-accent-fourth/40 border-accent-fourth/40',
      fifth: 'bg-accent-fifth/40 border-accent-fifth/40',
      sixth: 'bg-accent-sixth/40 border-accent-sixth/40'
    },
    size: {
      xs: 'w-6 h-6 text-xs',
      sm: 'w-7.5 h-7.5 text-sm',
      md: 'w-10 h-10 text-md',
      lg: 'w-12 h-12 text-xl',
      xl: 'w-16 h-16 text-3xl'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});

const MascotAvatarVariants = cva(
  'rounded-full border-0 shrink-0 aspect-square overflow-hidden flex items-center justify-center',
  {
    variants: {
      size: {
        xs: 'w-8 h-8',
        sm: 'w-12 h-12',
        md: 'w-16 h-16',
        lg: 'w-24 h-24',
        xl: 'w-36 h-36'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

type AvatarVariantprops = VariantProps<typeof AvatarVariants>;

export interface AvatarProps extends AvatarVariantprops {
  src?: string | undefined;
  accent?: AccentColor | undefined;
  mascot?: MascotName | undefined;
  name?: string | undefined;
  className?: string | undefined;
}

const blendOverWhite = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c: number) => Math.round(255 * (1 - alpha) + c * alpha);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

export const Avatar: React.FC<AvatarProps> = ({
  src,
  accent,
  name,
  mascot,
  variant,
  size,
  className
}) => {
  if (mascot) {
    const MascotSvg = MascotMap[mascot];
    const effectiveAccent: AccentColor = accent ?? 'first';
    const accentColor = colors.accent[effectiveAccent];
    const tinted = blendOverWhite(accentColor, 0.4);
    const style = {
      '--mascot-accent': accentColor,
      backgroundColor: tinted
    } as React.CSSProperties;
    return (
      <div className={cn(MascotAvatarVariants({ size }), className)} style={style}>
        <MascotSvg role='img' aria-label={name ?? mascot} className='w-full h-full' />
      </div>
    );
  }

  const classes = cn(AvatarVariants({ variant, size, accent }), className);

  if (src) {
    return <img src={src} alt={name ?? ''} className={classes} />;
  }

  const text = name ? getInitials(name) : '?';
  return <div className={classes}>{text}</div>;
};
Avatar.displayName = 'Avatar';
